import React, { useEffect, useState, useRef } from 'react';
import ShareSalesReportModal from '../../../components/Modals/ShareSalesReportModal';
import SalesTrendingReportService from '../../../services/SalesTrendingReportService';
import GearSpinner from '../../../components/GearSpinner/GearSpinner';
import Loader from '../../../components/Loader/Loader';
import SalesBarChart from '../../../components/Chart/SalesBarChart';
import shareIcon from '../../../assets/images/share-img.png';
import pdfIcon from '../../../assets/images/pdf-img.png';
import xlsIcon from '../../../assets/images/xls-img.png';
import moment from 'moment';

const DashboardTab = ({ active, topCustomers, totalSalesData, categories, loading, showDataFor }) => {
    // Local state to manage the specific data/loading state for each category
    const [showLoader, setShowLoader] = useState(false);
    const [localCategories, setLocalCategories] = useState([]);
    
    // 1. New State: Track if exporting is allowed
    const [isExportReady, setIsExportReady] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [sharePayload, setSharePayload] = useState(null);

    const salesTrendingReportService = new SalesTrendingReportService();
    const chartRefs = useRef({}); // Keep refs stable

    // Sync props to local state when categories arrive from parent
    useEffect(() => {
        if (categories.length > 0) {
            setLocalCategories(categories);
        } else {
            setLocalCategories([]);
        }
        // Reset refs when categories change to avoid stale keys
        chartRefs.current = {};
    }, [categories]);

    // 2. Effect: Check if ALL data (Total Sales + Categories) is loaded
    useEffect(() => {
        // Check 1: Is Total Sales Data loaded?
        const isTotalSalesLoaded = totalSalesData && totalSalesData.monthly_sales;

        // Check 2: Are Top Customers loaded? (loading prop from parent)
        const isTableLoaded = !loading;

        // Check 3: Are all Categories loaded?
        // We ensure we have categories, and NONE of them are currently loading
        const areCategoriesLoaded = localCategories.length > 0 && !localCategories.some(cat => cat.is_loader);

        if (isTotalSalesLoaded && isTableLoaded && areCategoriesLoaded) {
            setIsExportReady(true);
        } else {
            setIsExportReady(false);
        }
    }, [totalSalesData, loading, localCategories]);

    // Fetch details for each category once the list is populated
    useEffect(() => {
        if (active && localCategories.length > 0) {
            localCategories.forEach(category => {
                // Only fetch if we haven't loaded data yet
                if (category.is_loader) {
                    fetchCategorySales(category.name);
                }
            });
        }
    }, [localCategories, active]);

    if (!active) return null;

    const setChartRef = (key) => (el) => {
        if (el) {
            chartRefs.current[key] = el;
        } else {
            delete chartRefs.current[key];
        }
    };

    const collectCharts = () => {
        const images = [];
        const keys = Object.keys(chartRefs.current);
        
        // Validation: Expect 1 Main Chart + N Category Charts
        const expectedCount = localCategories.length + 1;

        if (keys.length < expectedCount) {
            console.warn(`Charts not ready. Expected: ${expectedCount}, Found: ${keys.length}`);
            alert('Charts are still rendering. Please wait a few seconds and try again.');
            return null;
        }

        keys.forEach((key) => {
            const chart = chartRefs.current[key];
            if (chart && typeof chart.getImage === 'function') {
                const img = chart.getImage();
                if (img) {
                    images.push({ key, image: img });
                }
            }
        });

        return images;
    };

    const buildPayload = (charts) => ({
        charts: charts, // Pass collected charts here
        table_data: topCustomers,
        tab: 'dashboard',
        meta: {
            portal_user_name: showDataFor,
            generated_at: new Date().toISOString()
        }
    });

    const handleDownloadPDF = async () => {
        // 3. Prevent click if data isn't ready
        if (!isExportReady) return;

        const charts = collectCharts();
        if (!charts) return; // Stop if charts failed to collect

        setShowLoader(true);
        try {
            salesTrendingReportService.exportSalesTrendingPdf(buildPayload(charts)).then((response) => {
                const blob = response.data;
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                const formattedDate = moment().format('MM-DD-YYYY_HH-mm');
                const title = `${showDataFor}-sales-trending-report-${formattedDate}`;
                link.href = url;
                link.download = `${title}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                setShowLoader(false);
            }).catch((error) => {
                console.error('Error downloading file:', error);
                setShowLoader(false);
            });
        } catch (error) {
            console.error('Error downloading datasheet:', error);
            setShowLoader(false);
        }
    };

    const handleDownloadExcel = async () => {
        if (!isExportReady) return; // Guard clause

        const charts = collectCharts();
        if (!charts) return;

        setShowLoader(true);

        try {
            salesTrendingReportService.exportSalesTrendingExcel(buildPayload(charts)).then((response) => {
                const blob = response.data;
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                const formattedDate = moment().format('MM/DD/YYYY HH:mm');
                const title = `${showDataFor}-sales-trending-report-${formattedDate}`;
                link.href = url;
                link.download = `${title}.xlsx`;
                link.click();
                window.URL.revokeObjectURL(url);
                setShowLoader(false);

            });
        } catch (error) {
            console.error('Error downloading datasheet:', error);
            setShowLoader(false);
        }
    }

    const handleShareClick = () => {
        const charts = collectCharts();
        if (!charts) return; // Stop if charts not ready

        const payload = buildPayload(charts);
        setSharePayload(payload);
        setShowShareModal(true);
    };

    const fetchCategorySales = (categoryName) => {
        var payload = {
            "portal_user_name": showDataFor,
            "current_month": moment().format('YYYY-MM'),
            "category": categoryName
        };
        
        salesTrendingReportService.getCategorySales(payload).then((response) => {
            if (response.status === 200) {
                if (response.data.status === 'success') {
                    setLocalCategories(prevCategories => prevCategories.map(cat => {
                        if (cat.name === categoryName) {
                            return {
                                ...cat,
                                is_loader: false,
                                data: response.data.result
                            };
                        }
                        return cat;
                    }));
                } else {
                    console.error('Error:', response.data.message);
                }
            } else {
                console.error('HTTP Error:', response.status);
            }
        });
    }

    return (
        <div className="container tab-pane active">
            <div className="row agencyDiv mb-4">
                <div className="col-md-12 text-center">
                    <button className="btn-link-style"
                        onClick={handleShareClick}
                        disabled={!isExportReady} 
                        style={{ 
                            opacity: isExportReady ? 1 : 0.5, 
                            cursor: isExportReady ? 'pointer' : 'not-allowed' 
                        }}
                    >
                        <img src={shareIcon} alt="share"/>Share
                    </button>
                    <button 
                        className="btn-link-style" 
                        onClick={handleDownloadExcel} 
                        disabled={!isExportReady} // Disable until charts are ready
                        style={{ opacity: isExportReady ? 1 : 0.5, cursor: isExportReady ? 'pointer' : 'not-allowed' }}
                    >
                        <img src={xlsIcon} alt="Excel" />Export Excel
                    </button>
                    
                    {/* 4. Disabled State Styling for PDF Button */}
                    <button 
                        className="btn-link-style" 
                        onClick={handleDownloadPDF}
                        disabled={!isExportReady} 
                        style={{ 
                            opacity: isExportReady ? 1 : 0.5, 
                            cursor: isExportReady ? 'pointer' : 'not-allowed' 
                        }}
                    >
                        <img src={pdfIcon} alt="Pdf" />Export PDF
                    </button>
                </div>
            </div>
            <div className="row">
                <div className="col-md-6">
                    <div className="techtop-graphdiv">
                        {totalSalesData.monthly_sales === undefined && (
                            <div className="text-center py-4">
                                <GearSpinner />
                            </div>
                        )}

                        {totalSalesData.monthly_sales && (
                            <SalesBarChart 
                                data={totalSalesData}
                                chatTitle={`Total Sales Data for ${showDataFor}`}
                                ref={setChartRef('total_sales')}
                            />
                        )}
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="techtop-tablediv">

                        {loading && (
                            <div className="text-center py-4">
                                <GearSpinner />
                            </div>
                        )}

                        {!loading && topCustomers.length === 0 && (
                            <div className="text-center py-4">
                                No data available
                            </div>
                        )}

                        {!loading && topCustomers.length > 0 && (
                            <table className="table table-bordered">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>Top 15 Customers - Past 12 Months</th>
                                        <th>Category</th>
                                        <th>12 Month Sales</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {topCustomers.map((customer, index) => (
                                        <tr key={index}>
                                            <td>{index + 1}</td>
                                            <td>{customer.customer_name}</td>
                                            <td>{customer.category}</td>
                                            <td>${customer.formatted_sales}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                    </div>
                </div>
            </div>
            <hr />
            {Array.from({ length: Math.ceil(localCategories.length / 2) }).map((_, rowIndex) => {
                const rowCategories = localCategories.slice(rowIndex * 2, rowIndex * 2 + 2);
                return (
                    <React.Fragment key={rowIndex}>
                        {/* The Row */}
                        <div className="row">
                            {rowCategories.map((category, colIndex) => (
                                <div key={colIndex} className="col-md-6">
                                    <div className="techtop-graphdiv">
                                        { (category.is_loader && category.data.length === 0) ? (
                                            <GearSpinner />
                                        ) : (
                                            <SalesBarChart 
                                                data={category.data} 
                                                chatTitle={`${category.name} Sales Trend - $${category.data.total_sales_formatted} YTD`} 
                                                ref={setChartRef(`category_${category.name}`)}
                                            />
                                        ) }
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* The Horizontal Rule (Separator) */}
                        <hr />
                    </React.Fragment>
                );
            })}
            {showLoader && (
                <Loader isLoading={showLoader} message="Processing..."/>
            )}
            {sharePayload && (
                <ShareSalesReportModal 
                    payload={sharePayload} 
                    isShow={showShareModal} 
                    onClose={() => setShowShareModal(false)} 
                />
            )}
        </div>
    );
};

export default DashboardTab;